import React from 'react';
import { Pagination, Row, StylovyzeHeader } from '@innovyze/lib-am-common';

import { paginationInitialState } from '@innovyze/lib-am-common/Reducers';
import { Help } from '@mui/icons-material';
import { StylovyzeTooltip } from '@innovyze/stylovyze';
import { TFunc } from '@Translations/types';

// Columns in order displayed by table
export enum Column {
	Locked = 0,
	AssetId,
	FinalRehabAction,
	CostSource, // 3 - new
	CostTable, // 4 - new
	TotalCost, // 5
	CostCalculation, // 6
	DraftRehabAction, // 7
	Notes, // 8
	MenuCell, // 9
	// I360A-2218: appended at the end so indices above never shift when the
	// 'i360-rehab-defect-based-costs' flag is off.
	CalculationMethod, // 10
	RehabLengthM, // 11
	RehabLengthStatus, // 12
}

export interface RehabResultColumnDef {
	/**
	 * Grid row-object field key emitted by the table's row mapping. Undefined
	 * for the menu column, which carries no data field. For most columns this
	 * also matches the corresponding `GridColDef.field`; the Locked column is
	 * the one exception (`'locked'` here vs. `'Locked'` on its `GridColDef`)
	 * because its custom `renderCell` reads `row.locked` directly instead of
	 * going through the grid's field-based value lookup.
	 */
	readonly field?: string;
	/**
	 * Resolves this column's translated header label. Written as a literal
	 * `t('...')` call (not a raw string handed to `t` elsewhere) so the
	 * i18next extractor can still find it via static scanning. Returns '' for
	 * the lock, menu and trench-only columns whose grid headers render custom
	 * nodes or are blank.
	 */
	readonly label: (t: TFunc) => string;
	/** Extracts this column's cell value from a raw API row. */
	readonly getCell: (row: Row, unit: string) => string | number;
}

// I360A-2420 adds camelCase trench fields (calculationMethod, rehabLengthM,
// trenchCalculation) alongside the existing UPPER_SNAKE_CASE ones on the raw
// API row - cast since the shared `Row` type only declares string-keyed
// string values.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const asTrenchRow = (row: Row): any => row;

/**
 * Single source of truth for the rehab result-grid columns, in `Column`
 * order (array index === `Column` enum value). Both consumers derive from
 * this: `mapRowToCells` (raw API row -> ordered cell array) and the
 * `RehabResultTable` grid (row-object mapping + header labels). Keep the
 * order in step with the `Column` enum above.
 */
export const REHAB_RESULT_COLUMNS: readonly RehabResultColumnDef[] = [
	{ field: 'locked', label: () => '', getCell: row => row?.LOCKED || '' },
	{
		field: 'ASSET_ID',
		label: t => t('Asset ID'),
		getCell: row => row?.ASSET_ID || '',
	},
	{
		field: 'FINAL_RRHAB_ACTION',
		label: t => t('Rehab Action'),
		getCell: row => row?.FINAL_RRHAB_ACTION || '',
	},
	{
		field: 'COST_SOURCE',
		label: t => t('Cost Source'),
		getCell: row => row?.COST_SOURCE || '',
	},
	{
		field: 'COST_TABLE',
		label: t => t('Cost Table'),
		getCell: row => row?.COST_TABLE_NAME || '',
	},
	{
		field: 'TOTAL_COST',
		label: t => t('Total Cost'),
		getCell: row => row?.TOTAL_COST,
	},
	{
		field: 'COST_CALCULATION',
		label: t => t('Cost Calculation'),
		getCell: (row, unit) =>
			unit == 'Metric'
				? row?.COST_CALCULATION_METER
				: row?.COST_CALCULATION_FT,
	},
	{
		field: 'ACTION_NAME',
		label: t => t('Draft Rehab Action'),
		getCell: row => row?.DRAFT_RRHAB_ACTION || '',
	},
	{ field: 'NOTES', label: t => t('Notes'), getCell: row => row?.NOTES || '' },
	// MenuCell: rendered by the grid's menu column, no backing data field.
	{ label: () => '', getCell: () => '' },
	{
		field: 'CALCULATION_METHOD',
		label: () => '',
		getCell: row => asTrenchRow(row)?.calculationMethod || '',
	},
	{
		field: 'REHAB_LENGTH_M',
		label: t => t('Rehab Length'),
		getCell: row => {
			const value = asTrenchRow(row)?.rehabLengthM;
			return value == null ? '' : String(value);
		},
	},
	{
		field: 'REHAB_LENGTH_STATUS',
		label: () => '',
		getCell: row => asTrenchRow(row)?.trenchCalculation?.dataStatus || '',
	},
];

/** Translated header label for a column — the single lookup into REHAB_RESULT_COLUMNS. */
export const columnLabel = (column: Column, t: TFunc): string =>
	REHAB_RESULT_COLUMNS[column].label(t);

export const mapRowToCells = (row: Row, unit: string): string[] =>
	REHAB_RESULT_COLUMNS.map(column => column.getCell(row, unit)) as string[];
export const lockToolTip = (t: TFunc) => {
	return (
		<StylovyzeTooltip
			heading={t('What does the lock mean?')}
			content={t(
				'Locking the rehab action will prevent manual override of assigned action/cost. This will also prevent changes to the assigned action when the decision tree is run.',
			)}
			padded>
			<Help
				fontSize="small"
				style={{ color: '#768D95', marginLeft: '3px' }}
			/>
		</StylovyzeTooltip>
	);
};

const autoHeader = (
	key: string,
	cell: StylovyzeHeader['cell'],
): StylovyzeHeader => ({
	key,
	cell,
	width: 'auto',
	align: 'left',
	sortable: false,
	padding: '0 10px',
});

export const headerReactNodes = (unit: string, t: TFunc): StylovyzeHeader[] => {
	// Captions resolve through the shared columnLabel helper so they are
	// defined once. Keys stay local: the filter helpers key off the
	// unit-dependent COST_CALCULATION_* fields, unlike the grid.
	const label = (column: Column) => columnLabel(column, t);
	return [
		{
			key: 'Locked',
			cell: lockToolTip(t),
			maxWidth: '50px',
			align: 'left',
			sortable: false,
			padding: '0 10px',
		},
		{
			key: 'ASSET_ID',
			cell: label(Column.AssetId),
			maxWidth: '200px',
			align: 'left',
			sortable: false,
			padding: '0 10px',
		},
		autoHeader('FINAL_RRHAB_ACTION', label(Column.FinalRehabAction)),
		autoHeader('COST_SOURCE', label(Column.CostSource)),
		autoHeader('COST_TABLE', label(Column.CostTable)),
		autoHeader('TOTAL_COST', label(Column.TotalCost)),
		autoHeader(
			unit == 'Metric' ? 'COST_CALCULATION_METER' : 'COST_CALCULATION_FT',
			label(Column.CostCalculation),
		),
		autoHeader('DRAFT_RRHAB_ACTION', label(Column.DraftRehabAction)),
		autoHeader('NOTES', label(Column.Notes)),
		{
			key: 'menu',
			cell: '',
			sortable: false,
			width: '68px',
			align: 'center',
			padding: '0 10px',
		},
	];
};

export const paginationDefault = (): Pagination => ({
	...paginationInitialState,
	sortKey: 'LAST_RUN',
	sortDescending: true,
});
